import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Hammer, Loader2, Mail, Lock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    if (mode === "signup" && !agreed) {
      toast.error("Please agree to the Marketplace Policy to continue");
      return;
    }
    if (mode === "signup") localStorage.setItem("sellend_pending_consent", "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup" && !agreed) {
      toast.error("Please agree to the Marketplace Policy to continue");
      return;
    }
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, we've sent a reset link.");
        setMode("signin");
      } else {
        const endpoint = mode === "signup" ? "/auth/register" : "/auth/login";
        const payload = mode === "signup"
          ? { name: form.name, email: form.email, password: form.password }
          : { email: form.email, password: form.password };
        const res = await api.post(endpoint, payload);
        if (res.data.session_token) localStorage.setItem("session_token", res.data.session_token);
        if (mode === "signup") {
          try { await api.post("/policy/consent", { version: "1.0" }); } catch {}
        }
        setUser(res.data);
        toast.success(mode === "signup" ? "Welcome to SelLend!" : "Welcome back!");
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden bg-[#0A0A0A]">
        <img
          src="https://images.unsplash.com/photo-1590880795696-20c7dfadacde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Rent it. Sell it.<br />Earn from your gear.
          </h2>
          <p className="mt-3 text-white/70 max-w-md">List items to rent by the day or sell outright — all in one place.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#FF5A00] flex items-center justify-center">
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold tracking-tight">SelLend</span>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset password" : "Welcome back"}
          </h1>
          <p className="text-muted-foreground mt-2 mb-8">
            {mode === "signup" ? "Join SelLend to rent, buy and sell gear."
              : mode === "forgot" ? "Enter your email and we'll send a reset link."
              : "Sign in to list, rent, buy and sell gear."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "signup" && (
              <div className="relative">
                <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="auth-name" value={form.name} onChange={set("name")} placeholder="Full name" className="pl-9 h-12 bg-white border-black/10 rounded-xl" />
              </div>
            )}
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="auth-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className="pl-9 h-12 bg-white border-black/10 rounded-xl" />
            </div>
            {mode !== "forgot" && (
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="auth-password" type="password" value={form.password} onChange={set("password")} placeholder="Password" className="pl-9 h-12 bg-white border-black/10 rounded-xl" />
              </div>
            )}
            {mode === "signin" && (
              <div className="text-right">
                <button type="button" data-testid="forgot-link" onClick={() => setMode("forgot")} className="text-sm text-muted-foreground hover:text-[#FF5A00] transition-colors">Forgot password?</button>
              </div>
            )}
            {mode === "signup" && (
              <label data-testid="policy-agree-label" className="flex items-start gap-2.5 text-sm text-muted-foreground cursor-pointer pt-1">
                <input
                  type="checkbox"
                  data-testid="policy-agree-checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-black/30 text-[#FF5A00] focus:ring-[#FF5A00] cursor-pointer shrink-0"
                />
                <span>
                  I have read and agree to the{" "}
                  <a href="/policy" target="_blank" rel="noopener noreferrer" data-testid="policy-agree-link" className="font-semibold text-[#0A0A0A] hover:text-[#FF5A00] underline">
                    Marketplace Policy
                  </a>.
                </span>
              </label>
            )}
            <button data-testid="auth-submit" disabled={busy || (mode === "signup" && !agreed)} className="w-full h-12 flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-full font-medium hover:bg-[#FF5A00] transition-colors active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-black/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-black/10" />
              </div>
              <button
                data-testid="google-login-btn"
                onClick={googleLogin}
                className="w-full h-12 flex items-center justify-center gap-3 bg-white border border-black/15 rounded-full font-medium hover:border-black transition-colors active:scale-95"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                Continue with Google
              </button>
            </>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "signin" && <>New to SelLend? <button data-testid="switch-signup" onClick={() => setMode("signup")} className="font-semibold text-[#0A0A0A] hover:text-[#FF5A00]">Create an account</button></>}
            {mode === "signup" && <>Already have an account? <button data-testid="switch-signin" onClick={() => setMode("signin")} className="font-semibold text-[#0A0A0A] hover:text-[#FF5A00]">Sign in</button></>}
            {mode === "forgot" && <button data-testid="back-signin" onClick={() => setMode("signin")} className="font-semibold text-[#0A0A0A] hover:text-[#FF5A00]">Back to sign in</button>}
          </p>
        </div>
      </div>
    </div>
  );
}
